// Lazy overlay kit (Phase 14 performance budget, spec §15). Tooltips, menus, popovers, dialogs and sheets render their
// trigger here, in the first-paint bundle, with the same props and ARIA state Radix gives it; the Radix content (Popper
// / Floating UI, DismissableLayer, FocusScope, RemoveScroll, Menu…) lives in one lazy chunk (`kit.tsx`) that is
// anchored to the real trigger element through `virtualRef`. The trigger element is never re-parented or remounted
// when the kit arrives — the floating content renders as its sibling. The kit is fetched on the first hover / focus /
// open, or ahead of time with `preloadOverlays()` (the web app calls it once its first screen has settled); once
// loaded, every overlay renders synchronously.
import { useCallback, useEffect, useInsertionEffect, useRef, useState, useSyncExternalStore, type RefObject } from 'react';
import type * as Kit from './kit.tsx';

export type OverlayKit = typeof Kit;

/** Something overlay content needs besides the kit, e.g. app copy loaded after the first screen. */
export interface OverlayDependency {
  ready: () => boolean;
  load: () => Promise<unknown>;
}

// the kit as handed to components: its chunk and every dependency are there
let kit: OverlayKit | null = null;
let pending: Promise<OverlayKit> | null = null;
let generation = 0;
const dependencies: OverlayDependency[] = [];
const listeners = new Set<() => void>();
const notify = () => {
  for (const l of listeners) l();
};

/** Loads the overlay kit chunk and its dependencies (a failed load — offline, a deploy — may be retried). */
export function loadOverlays(): Promise<OverlayKit> {
  if (kit) return Promise.resolve(kit);
  if (!pending) {
    const gen = generation;
    pending = Promise.all([import('./kit.tsx'), ...dependencies.filter(d => !d.ready()).map(d => d.load())]).then(
      ([m]) => {
        // a dependency was added meanwhile: wait for it too
        if (gen !== generation) return loadOverlays();
        kit = m;
        notify();
        return m;
      },
      (err: unknown) => {
        if (gen === generation) pending = null;
        throw err;
      },
    );
  }
  return pending;
}

/**
 * Overlays render only once `dep` is ready as well (menus built from app copy loaded lazily never show raw keys). A
 * kit already handed out waits for it, unless it is ready.
 */
export function addOverlayDependency(dep: OverlayDependency): void {
  dependencies.push(dep);
  if (dep.ready()) return;
  generation++;
  pending = null;
  if (kit) {
    kit = null;
    notify();
    void preloadOverlays();
  }
}

/** Fetches the kit ahead of the first overlay (e.g. when the browser is idle after the first screen). */
export function preloadOverlays(): Promise<void> {
  return loadOverlays().then(
    () => undefined,
    () => undefined,
  );
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};
const snapshot = () => kit;

/** The kit once loaded (`null` before); `want` starts loading it. */
export function useOverlayKit(want: boolean): OverlayKit | null {
  const k = useSyncExternalStore(subscribe, snapshot, snapshot);
  useEffect(() => {
    if (want && !k) void preloadOverlays();
  }, [want, k]);
  return k;
}

// Keyboard modality, tracked from the start like Radix Menu does for each mounted menu root: a menu whose (lazy) root
// mounts only when it opens would otherwise miss the keydown that opened it and not focus its first item.
let usingKeyboard = false;
if (typeof document !== 'undefined') {
  const onPointer = () => {
    usingKeyboard = false;
  };
  document.addEventListener(
    'keydown',
    () => {
      usingKeyboard = true;
      document.addEventListener('pointerdown', onPointer, { capture: true, once: true });
      document.addEventListener('pointermove', onPointer, { capture: true, once: true });
    },
    { capture: true },
  );
}
/** Whether the last interaction was the keyboard (until the next pointer down / move). */
export const isUsingKeyboard = () => usingKeyboard;

/**
 * Radix `useControllableState` (same semantics): controlled when `prop` is defined — `onChange` for actual changes
 * only —, otherwise local state whose changes reach `onChange` after the commit.
 */
export function useControllableState<T>(prop: T | undefined, defaultProp: T, onChange?: (value: T) => void): [T, (next: T | ((prev: T) => T)) => void] {
  const [local, setLocal] = useState(defaultProp);
  const onChangeRef = useRef(onChange);
  useInsertionEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  const prevLocal = useRef(local);
  useEffect(() => {
    if (prevLocal.current !== local) {
      onChangeRef.current?.(local);
      prevLocal.current = local;
    }
  }, [local]);
  const controlled = prop !== undefined;
  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      if (controlled) {
        const value = typeof next === 'function' ? (next as (prev: T) => T)(prop) : next;
        if (value !== prop) onChangeRef.current?.(value);
      } else setLocal(next);
    },
    [controlled, prop],
  );
  return [controlled ? prop : local, setValue];
}

/**
 * While an overlay is open but its content still waits for the kit, an outside pointer down or Escape closes it (what
 * its DismissableLayer would do), so it does not appear later after the user moved on. `inside`: the trigger, whose
 * own handlers toggle.
 */
export function useDismissWhilePending(pending: boolean, close: () => void, inside?: RefObject<HTMLElement | null>): void {
  const latest = useRef(close);
  useInsertionEffect(() => {
    latest.current = close;
  }, [close]);
  useEffect(() => {
    if (!pending) return;
    const onPointer = (e: PointerEvent) => {
      if (!(e.target instanceof Node && inside?.current?.contains(e.target))) latest.current();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') latest.current();
    };
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [pending, inside]);
}

/** Keeps a lazily mounted overlay mounted after it first opened (exit animations, Presence). */
export function useOpenedOnce(open: boolean): boolean {
  const [opened, setOpened] = useState(open);
  if (open && !opened) setOpened(true);
  return opened || open;
}
